import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  Plus,
  Clock,
  DollarSign,
  Check,
  X,
  AlertTriangle,
  Calendar,
  FileText,
  Loader2,
  User,
  RefreshCw,
} from "lucide-react";

const CHANGE_TYPES = [
  { value: "deadline_change", label: "Deadline Change", icon: Calendar },
  { value: "budget_change", label: "Budget/Price Change", icon: DollarSign },
  { value: "scope_change", label: "Scope Change", icon: RefreshCw },
  { value: "artist_change", label: "Artist Change", icon: User },
  { value: "other", label: "Other", icon: FileText },
];

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("uk-UA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("uk-UA", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const TaskChangeRequests = ({ 
  taskId, 
  taskName,
  currentDeadline, 
  currentPrice,
  currentClientPrice,
  taskCurrency = "USD",
  canApprove = false,
  onChangeApplied,
  compact = false 
}) => {
  const [changeRequests, setChangeRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [newRequest, setNewRequest] = useState({
    description: "",
    change_type: "budget_change",
    original_deadline: currentDeadline || "",
    new_deadline: "",
    original_price: currentPrice || 0,
    new_price: "",
    original_client_price: currentClientPrice || 0,
    new_client_price: "",
    currency: taskCurrency,
    reason: "",
  });

  useEffect(() => {
    fetchChangeRequests();
  }, [taskId]);

  useEffect(() => {
    setNewRequest(prev => ({
      ...prev,
      original_deadline: currentDeadline || "",
      original_price: currentPrice || 0,
      original_client_price: currentClientPrice || 0,
      currency: taskCurrency,
    }));
  }, [currentDeadline, currentPrice, currentClientPrice, taskCurrency]);

  const fetchChangeRequests = async () => {
    try {
      const res = await api.get(`/tasks/${taskId}/change-requests`);
      setChangeRequests(res.data);
    } catch (error) {
      console.error("Error fetching task change requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!newRequest.description.trim()) {
      toast.error("Description is required");
      return;
    }

    // Validate based on change type
    if (newRequest.change_type === "deadline_change" && !newRequest.new_deadline) {
      toast.error("New deadline is required for deadline changes");
      return;
    }
    // For budget changes, require at least client price OR artist price
    if (newRequest.change_type === "budget_change" && !newRequest.new_client_price && !newRequest.new_price) {
      toast.error("New client price or artist price is required for budget changes");
      return;
    }

    setCreating(true);
    try {
      await api.post(`/tasks/${taskId}/change-requests`, {
        task_id: taskId,
        description: newRequest.description,
        change_type: newRequest.change_type,
        original_deadline: newRequest.original_deadline || null,
        new_deadline: newRequest.new_deadline || null,
        original_price: parseFloat(newRequest.original_price) || null,
        new_price: newRequest.new_price ? parseFloat(newRequest.new_price) : null,
        original_client_price: parseFloat(newRequest.original_client_price) || null,
        new_client_price: newRequest.new_client_price ? parseFloat(newRequest.new_client_price) : null,
        currency: newRequest.currency,
        reason: newRequest.reason || null,
      });
      toast.success("Change request created");
      setDialogOpen(false);
      setNewRequest({
        description: "",
        change_type: "budget_change",
        original_deadline: currentDeadline || "",
        new_deadline: "",
        original_price: currentPrice || 0,
        new_price: "",
        original_client_price: currentClientPrice || 0,
        new_client_price: "",
        currency: taskCurrency,
        reason: "",
      });
      fetchChangeRequests();
    } catch (error) {
      const errorDetail = error?.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        toast.error(errorDetail.map(e => e.msg || e.message || String(e)).join(", "));
      } else {
        toast.error(errorDetail || "Failed to create request");
      }
    } finally {
      setCreating(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await api.put(`/tasks/${taskId}/change-requests/${requestId}`, { status: "approved" });
      toast.success("Change request approved");
      fetchChangeRequests();
      if (onChangeApplied) onChangeApplied();
    } catch (error) {
      toast.error("Failed to approve request");
    }
  };

  const handleReject = async (requestId) => {
    try {
      await api.put(`/tasks/${taskId}/change-requests/${requestId}`, { status: "rejected" });
      toast.success("Change request rejected");
      fetchChangeRequests();
    } catch (error) {
      toast.error("Failed to reject request");
    }
  };

  const pendingCount = changeRequests.filter(r => r.status === "pending").length;

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Pending</Badge>;
      case "approved":
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Approved</Badge>;
      case "rejected":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getChangeTypeInfo = (type) => {
    return CHANGE_TYPES.find(t => t.value === type) || CHANGE_TYPES[4];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-400" />
            <span className="text-sm font-medium text-zinc-300">Change Requests</span>
            {pendingCount > 0 && (
              <Badge className="bg-orange-500/20 text-orange-400 text-xs">{pendingCount} pending</Badge>
            )}
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="h-7 text-xs border-zinc-700 hover:bg-zinc-800">
                <Plus className="h-3 w-3 mr-1" />
                New
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-zinc-50">New Change Request</DialogTitle>
              </DialogHeader>
              {renderCreateForm()}
            </DialogContent>
          </Dialog>
        </div>

        {changeRequests.length === 0 ? (
          <p className="text-xs text-zinc-500">No change requests</p>
        ) : (
          <div className="space-y-1">
            {changeRequests.slice(0, 3).map(req => (
              <div 
                key={req.id} 
                className={cn(
                  "flex items-center justify-between p-2 rounded bg-zinc-800/50 text-xs",
                  req.status === "pending" && "border border-yellow-500/30"
                )}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {React.createElement(getChangeTypeInfo(req.change_type).icon, { 
                    className: "h-3 w-3 text-zinc-400 flex-shrink-0" 
                  })}
                  <span className="truncate text-zinc-300">{req.description}</span>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  {getStatusBadge(req.status)}
                  {req.status === "pending" && canApprove && (
                    <>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-6 w-6 p-0 text-green-400 hover:text-green-300"
                        onClick={() => handleApprove(req.id)}
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                        onClick={() => handleReject(req.id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {changeRequests.length > 3 && (
              <p className="text-xs text-zinc-500 text-center">+{changeRequests.length - 3} more</p>
            )}
          </div>
        )}
      </div>
    );
  }

  function renderCreateForm() {
    return (
      <div className="space-y-4">
        <div>
          <Label className="text-zinc-400 text-sm">Change Type</Label>
          <Select
            value={newRequest.change_type}
            onValueChange={(v) => setNewRequest({ ...newRequest, change_type: v })}
          >
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50 mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              {CHANGE_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value} className="text-zinc-50">
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-zinc-400 text-sm">Description *</Label>
          <Textarea
            value={newRequest.description}
            onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
            placeholder="Describe the change..."
            className="bg-zinc-800 border-zinc-700 text-zinc-50 mt-1"
            rows={3}
          />
        </div>

        {(newRequest.change_type === "deadline_change" || newRequest.change_type === "scope_change") && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-sm">Original Deadline</Label>
              <Input
                type="date"
                value={newRequest.original_deadline?.split('T')[0] || ""}
                disabled
                className="bg-zinc-800/50 border-zinc-700 text-zinc-400 mt-1"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-sm">New Deadline *</Label>
              <Input
                type="date"
                value={newRequest.new_deadline}
                onChange={(e) => setNewRequest({ ...newRequest, new_deadline: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50 mt-1"
              />
            </div>
          </div>
        )}

        {(newRequest.change_type === "budget_change" || newRequest.change_type === "scope_change") && (
          <>
            {/* Currency selector */}
            <div>
              <Label className="text-zinc-400 text-sm">Currency</Label>
              <Select
                value={newRequest.currency}
                onValueChange={(v) => setNewRequest({ ...newRequest, currency: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50 mt-1 w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="UAH" className="text-zinc-50">₴ UAH</SelectItem>
                  <SelectItem value="USD" className="text-zinc-50">$ USD</SelectItem>
                  <SelectItem value="EUR" className="text-zinc-50">€ EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Client Price - Main focus */}
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <Label className="text-emerald-400 text-sm font-medium mb-2 block">Client Price (Total) - {newRequest.currency}</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">Current</Label>
                  <Input
                    type="number"
                    value={newRequest.original_client_price || ""}
                    disabled
                    className="bg-zinc-800/50 border-zinc-700 text-zinc-400 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">New Price *</Label>
                  <Input
                    type="number"
                    value={newRequest.new_client_price}
                    onChange={(e) => setNewRequest({ ...newRequest, new_client_price: e.target.value })}
                    placeholder="Enter new client price"
                    className="bg-zinc-800 border-emerald-500/50 text-zinc-50 mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Artist Price - Secondary */}
            <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
              <Label className="text-zinc-400 text-sm mb-2 block">Artist Price (optional) - {newRequest.currency}</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-500 text-xs">Current</Label>
                  <Input
                    type="number"
                    value={newRequest.original_price || ""}
                    disabled
                    className="bg-zinc-800/50 border-zinc-700 text-zinc-500 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-500 text-xs">New Price</Label>
                  <Input
                    type="number"
                    value={newRequest.new_price}
                    onChange={(e) => setNewRequest({ ...newRequest, new_price: e.target.value })}
                    placeholder="0"
                    className="bg-zinc-800 border-zinc-700 text-zinc-50 mt-1"
                  />
                </div>
              </div>
            </div>
          </>
        )}

        <div>
          <Label className="text-zinc-400 text-sm">Reason (optional)</Label>
          <Input
            value={newRequest.reason}
            onChange={(e) => setNewRequest({ ...newRequest, reason: e.target.value })}
            placeholder="Brief reason for the change"
            className="bg-zinc-800 border-zinc-700 text-zinc-50 mt-1"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => setDialogOpen(false)}
            className="border-zinc-700"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateRequest}
            disabled={creating}
            className="bg-orange-600 hover:bg-orange-700"
          >
            {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Request
          </Button>
        </div>
      </div>
    );
  }

  // Full view
  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium text-zinc-50 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-400" />
            Change Requests
            {pendingCount > 0 && (
              <Badge className="bg-orange-500/20 text-orange-400">{pendingCount} pending</Badge>
            )}
          </CardTitle>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-orange-600 hover:bg-orange-700">
                <Plus className="h-4 w-4 mr-1" />
                New Request
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-zinc-50">New Change Request</DialogTitle>
              </DialogHeader>
              {renderCreateForm()}
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {changeRequests.length === 0 ? (
          <div className="text-center py-6 text-zinc-500">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No change requests yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {changeRequests.map(req => {
              const typeInfo = getChangeTypeInfo(req.change_type);
              return (
                <div 
                  key={req.id}
                  className={cn(
                    "p-3 rounded-lg bg-zinc-800/50 border",
                    req.status === "pending" ? "border-yellow-500/30" : "border-zinc-700/50"
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {React.createElement(typeInfo.icon, { className: "h-4 w-4 text-zinc-400" })}
                        <span className="text-sm font-medium text-zinc-50">{typeInfo.label}</span>
                        {req.currency && req.currency !== "USD" && (
                          <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">{req.currency}</Badge>
                        )}
                        {getStatusBadge(req.status)}
                      </div>
                      <p className="text-sm text-zinc-300 mb-2">{req.description}</p>
                      
                      {/* Show deadline change */}
                      {req.new_deadline && (
                        <div className="flex items-center gap-2 text-xs text-zinc-400 mb-1">
                          <Calendar className="h-3 w-3" />
                          <span>Deadline: {formatDate(req.original_deadline)} → {formatDate(req.new_deadline)}</span>
                        </div>
                      )}
                      
                      {/* Show client price change (primary) */}
                      {req.new_client_price !== null && req.new_client_price !== undefined && (
                        <div className="flex items-center gap-2 text-xs text-emerald-400 mb-1">
                          <DollarSign className="h-3 w-3" />
                          <span className="font-medium">Client Price: {req.currency === "UAH" ? "₴" : req.currency === "EUR" ? "€" : "$"}{req.original_client_price?.toLocaleString() || 0} → {req.currency === "UAH" ? "₴" : req.currency === "EUR" ? "€" : "$"}{req.new_client_price?.toLocaleString()}</span>
                        </div>
                      )}
                      
                      {/* Show artist price change (secondary) */}
                      {req.new_price !== null && req.new_price !== undefined && (
                        <div className="flex items-center gap-2 text-xs text-zinc-400 mb-1">
                          <DollarSign className="h-3 w-3" />
                          <span>Artist Price: {req.currency === "UAH" ? "₴" : req.currency === "EUR" ? "€" : "$"}{req.original_price?.toLocaleString() || 0} → {req.currency === "UAH" ? "₴" : req.currency === "EUR" ? "€" : "$"}{req.new_price?.toLocaleString()}</span>
                        </div>
                      )}
                      
                      {req.reason && (
                        <p className="text-xs text-zinc-500 mt-1">Reason: {req.reason}</p>
                      )}
                      
                      <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {req.created_by_name}
                        </span>
                        <span>{formatDateTime(req.created_at)}</span>
                        {req.approved_by_name && (
                          <span className="text-green-400">
                            {req.status === "approved" ? "Approved" : "Rejected"} by {req.approved_by_name}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {req.status === "pending" && canApprove && (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                          onClick={() => handleApprove(req.id)}
                          title="Approve"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={() => handleReject(req.id)}
                          title="Reject"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TaskChangeRequests;
